import { MIN_PASSWORD_LENGTH } from "@ondo/shared";

/**
 * The three panels of the wizard, and the labels under the step markers.
 *
 * The third step is "Confirm email" rather than "Done" because that is what
 * happens there: the code is checked, and only then does the account get
 * created. The completed panel that follows is the same step, finished.
 */
export const SIGNUP_STEPS = [
  { key: "details", label: "Your details" },
  { key: "identity", label: "Verify identity" },
  { key: "email", label: "Confirm email" },
] as const;

export const COUNTRIES = [
  "United Kingdom",
  "United States",
  "France",
  "Germany",
  "Switzerland",
  "United Arab Emirates",
  "Hong Kong",
  "Singapore",
  "Italy",
  "Spain",
  "Netherlands",
  "Belgium",
  "Austria",
  "Japan",
  "Australia",
  "Canada",
  "Brazil",
  "India",
  "China",
  "South Korea",
  "Other",
] as const;

/** Documents the provider will ask for once the scan is live. */
export const IDENTITY_REQUIREMENTS = [
  {
    icon: "🪪",
    title: "Government-issued ID",
    detail: "Passport, driving licence or national ID card",
  },
  {
    icon: "🏠",
    title: "Proof of address",
    detail: "Utility bill or bank statement (dated within 3 months)",
  },
  {
    icon: "🔍",
    title: "AML screening",
    detail: "Automated check against global sanctions & PEP lists",
  },
] as const;

/** Digits in the emailed code. Matches CODE_LENGTH on the API. */
export const CODE_LENGTH = 6;

/**
 * Shortest password we accept. The API enforces the same floor.
 *
 * RE-EXPORTED FROM `@ondo/shared` SINCE PASSWORD RECOVERY, rather than declared here.
 * It was its own `= 8` beside the API's `MIN_ACCOUNT_PASSWORD_LENGTH`, and recovery
 * needed the same number on a third screen and on the phone — which is the point at
 * which a duplicated rule becomes a drifting one. The symptom of the drift would be
 * quiet: a form quoting one number while the server enforces another, so the refusal
 * names the wrong figure and nobody can act on it. `domain/password.ts` holds it.
 *
 * Still exported from here so every call site in this folder reads as it did. Imported
 * and re-exported rather than `export … from`, because `SIGNUP_COPY` below quotes the
 * number in a template string and a bare re-export does not put it in local scope.
 */
export { MIN_PASSWORD_LENGTH };

export const SIGNUP_COPY = {
  incomplete: "Please complete all required fields.",
  membershipRequired: "Choose at least one membership type.",
  passwordMismatch: "Passwords do not match.",
  passwordTooShort: `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
  emailInvalid: "That does not look like an email address.",
  codeIncomplete: `Enter the ${CODE_LENGTH}-digit code from your email.`,
  unknownError: "Something went wrong. Please try again.",
} as const;

/**
 * The stand-in scan code, row by row. Fixed rather than encoded so it costs no
 * dependency, and deliberately not scannable — see `QrPlaceholder`.
 */
export const QR_PLACEHOLDER_PATTERN = [
  "1111111010001111111",
  "1000001011101000001",
  "1011101000101011101",
  "1011101110101011101",
  "1011101001001011101",
  "1000001010101000001",
  "1111111010101111111",
  "0000000011000000000",
  "1101101101011010110",
  "0100011000110001101",
  "1110110011001101011",
  "0001001110100110010",
  "1101110001011011101",
  "0000000110010100011",
  "1111111011010110101",
  "1000001001101001100",
  "1011101110011011110",
  "1011101010100101001",
  "1111111011011101111",
];
